import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Input, { InputProps } from '..';
import Frame from '../../Frame';
import { IconAngle, IconLoader } from '../styles';
import * as S from '../styles';
import InputDropdownMultiple from './InputDropdownMultiple';

export type DropdownItem<T = any> = {
  id: number | string;
  name: number | string;
  type?: string;
  data?: T;
};

export interface InputDropdownProps extends InputProps {
  items?: DropdownItem[];
  onSelected?: (item: DropdownItem) => void;
  iconActive?: boolean;
  isTooltip?: boolean;
  loading?: boolean;
  onDebouncedChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
  fontSize?: number;
  hasClearItem?: boolean;
  listDirection?: 'up' | 'down';
}

const InputDropdown = ({
  onChange,
  onBlur,
  onSelected,
  className,
  iconActive,
  isTooltip,
  value,
  label,
  onFocus,
  placeholder = '',
  items = [],
  loading = false,
  mask = (value: any) => value,
  unmask = (value: any) => value,
  onDebouncedChange,
  fontSize,
  hasClearItem = false,
  listDirection: ld = 'down',
  disabled,
  ...rest
}: InputDropdownProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [hoverIcon, setHoverIcon] = useState(false);
  const frameRef = useRef<HTMLLabelElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // const markRef = useRef<HTMLDivElement>(null);
  const [frameHeight, setFrameHeight] = useState(0);
  const [hoverItem, setHoverItem] = useState(-1);
  const [inputValue, setInputValue] = useState(value ?? '');
  const [inputPlaceholder, setInputPlaceholder] = useState(placeholder ?? '');
  const [hoverValue, setHoverValue] = useState(value ?? '');
  const [inputWidth, setInputWidth] = useState(0);
  const [inputHeight, setInputHeight] = useState(0);
  const [inputTop, setInputTop] = useState(0);
  // const [markPosition, setMarkPosition] = React.useState({ x: 0, y: 0 });
  const [listDirection, setListDirection] = React.useState<'up' | 'down'>(ld);
  const [cursorInList, setCursorInList] = useState(false);
  const notFoundMessage = 'Não encontrado';
  const filtered = items.filter((item) =>
    String(item.name).toLowerCase().includes(String(inputValue).toLowerCase()),
  );
  const filteredItems = [
    ...(hasClearItem ? [{ id: -2, name: '--' }] : []),
    ...(filtered.length !== 0 ? filtered : [{ id: -1, name: notFoundMessage }]),
  ];

  const maskedFilteredItems = filteredItems.map((item) =>
    item.id !== -1 && item.id !== -2
      ? { ...item, name: mask(item.name) }
      : { ...item },
  );
  const isEmpty = maskedFilteredItems[0]?.name === notFoundMessage;

  const observer = React.useRef<IntersectionObserver | null>(null);

  // React.useEffect(() => {
  //   if (isFocused && listRef.current) {
  //     const { width, height } = listRef.current.getBoundingClientRect();
  //     setMarkPosition({
  //       x: width / 2,
  //       y: inputTop + inputHeight + height + 20,
  //     });
  //   }
  // }, [isFocused, frameHeight]);

  // const updateListDirection = React.useCallback(
  //   (entry: IntersectionObserverEntry) => {
  //     setListDirection(entry.isIntersecting ? 'down' : 'up');
  //   },
  //   [],
  // );

  // React.useEffect(() => {
  //   observer.current = new IntersectionObserver(([entry]) => {
  //     updateListDirection(entry);
  //   }, {});
  //   if (markRef.current) {
  //     observer.current.observe(markRef.current);
  //   }
  //   return () => {
  //     if (markRef.current && observer.current) {
  //       observer.current.unobserve(markRef.current);
  //     }
  //   };
  // }, [updateListDirection]);

  useEffect(() => {
    setInputPlaceholder(placeholder as any);
  }, [placeholder]);

  useEffect(() => {
    if (frameRef.current) {
      setFrameHeight(
        isFocused ? Math.min(frameRef.current.offsetHeight, 300) : 0,
      );
    }
  });

  useEffect(() => {
    if (inputRef.current) {
      setInputHeight(inputRef.current.offsetHeight);
      const { top } = inputRef.current.getBoundingClientRect();
      setInputTop(top);
    }
  }, [isFocused]);

  useEffect(() => {
    setInputValue((value as any) ?? '');
    setHoverValue((value as any) ?? '');
  }, [value]);

  useEffect(() => {
    if (value === '' && !isFocused) {
      setHoverValue('');
      setInputPlaceholder('');
    }
  }, [value, isFocused]);

  const dropdownListTop =
    inputTop +
    (listDirection === 'down' ? inputHeight + 10 : -(frameHeight + 10));

  let timerId: NodeJS.Timeout;

  React.useEffect(() => {
    if (!(isFocused && onDebouncedChange)) return;
    const handleInputChange = () => {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        onDebouncedChange({ target: { value: inputValue } } as any);
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(timerId);
    };
  }, [isFocused, inputValue]);

  return (
    <S.DropdownContainer className={`dropdown-container ${className}`}>
      <Input
        ref={inputRef}
        label={label}
        placeholder={mask(inputPlaceholder)}
        fontSize={fontSize}
        setFieldWidth={setInputWidth}
        onFocus={(e) => {
          if (disabled) return;
          setInputPlaceholder(hoverValue as any);
          setHoverValue('');
          setInputValue('');
          setIsFocused(true);
          onFocus && onFocus(e as any);
        }}
        onBlur={(e) => {
          if (!(hoverItem === 0 && hasClearItem) && !hoverValue) {
            setHoverValue(inputPlaceholder);
            onChange &&
              onChange({
                ...e,
                target: { ...e.target, value: `${inputPlaceholder}` },
              });
            setInputValue('');
            setInputPlaceholder('');
          }
          if (hoverItem === -1 && !hoverIcon) {
            if (cursorInList) {
              onChange &&
                onChange({
                  ...e,
                  target: { ...e.target, value: inputPlaceholder },
                });
              setInputValue(inputPlaceholder);
            }
            setIsFocused(false);
            onBlur && onBlur(e);
          }
        }}
        IconRight={
          <div
            style={{
              padding: '0 10px',
            }}
          >
            {loading ? (
              <IconLoader width={20} height={20} />
            ) : (
              <IconAngle
                onClick={() => {
                  if (disabled) return;
                  if (!isFocused) {
                    inputRef.current?.focus();
                  }
                  setIsFocused(!isFocused);
                }}
                $disabled={disabled}
                $isFocused={isFocused}
                onMouseEnter={() => {
                  setHoverIcon(true);
                }}
                onMouseLeave={() => {
                  setHoverIcon(false);
                }}
              />
            )}
          </div>
        }
        value={hoverValue}
        onChange={(e) => {
          const value = e.target.value;
          setInputValue(value);
          onChange && onChange(e);
        }}
        onKeyDown={(e) => {
          let item: DropdownItem;
          let newHoverItem: number;
          switch (e.key) {
            case 'ArrowDown':
              newHoverItem = Math.min(
                maskedFilteredItems.length,
                hoverItem + 1,
              );
              setHoverItem(newHoverItem);
              item = maskedFilteredItems[newHoverItem];
              if (item) {
                if (item.id === -2) {
                  setHoverValue('');
                } else {
                  setHoverValue(item.name);
                }
              }
              break;
            case 'ArrowUp':
              newHoverItem = Math.max(-1, hoverItem - 1);
              setHoverItem(newHoverItem);
              item = maskedFilteredItems[newHoverItem];
              if (item) {
                if (item.id === -2) {
                  setHoverValue('');
                } else {
                  setHoverValue(item.name);
                }
              }
              break;
            case 'Enter':
              item = filteredItems[hoverItem];
              if (item && item.id !== -2) {
                onSelected && onSelected(item);
              }
              setIsFocused(false);
              setHoverItem(-1);
              inputRef.current?.blur();
              break;
            default:
              break;
          }
        }}
        mask={mask}
        unmask={unmask}
        disabled={disabled}
        {...rest}
      />
      <S.DropdownList
        ref={listRef}
        $isFocused={isFocused}
        $height={frameHeight}
        $inputWidth={inputWidth}
        $top={dropdownListTop}
        $fontSize={fontSize}
        onMouseEnter={() => {
          setCursorInList(true);
        }}
        onMouseLeave={() => {
          setHoverValue(inputValue);
          setCursorInList(false);
        }}
      >
        {!isEmpty ? (
          maskedFilteredItems.map(({ id, name, type }, index) => (
            <S.DropdownItem
              key={index + 1}
              $isSelectedItem={
                mask(inputPlaceholder) === name || mask(inputValue) === name
              }
              $isHover={hoverItem === index}
              onMouseEnter={() => {
                if (!isEmpty) {
                  setHoverItem(index);
                  if (id === -2 && hasClearItem) {
                    setHoverValue('');
                  } else {
                    setHoverValue(maskedFilteredItems[index].name);
                  }
                }
              }}
              onMouseLeave={() => {
                setHoverItem(-1);
              }}
              onClick={() => {
                if (!(id === -1 || id === -2)) {
                  const item = filteredItems[index];
                  setInputValue(item.name);
                  // onChange &&
                  //   onChange({
                  //     target: { value: item.name },
                  //   } as ChangeEvent<HTMLInputElement>);
                  onSelected && onSelected(item);
                  setHoverItem(-1);
                }
                if (id === -2 && hasClearItem) {
                  setHoverItem(-1);
                  setHoverValue('');
                  setInputValue('');
                  setInputPlaceholder('');
                  // onChange &&
                  //   onChange({
                  //     target: { value: '' },
                  //   } as ChangeEvent<HTMLInputElement>);
                }
                if (!isEmpty) {
                  setIsFocused(false);
                }
              }}
            >
              {`${name}${type ? ` - ${type}` : ''}`}
            </S.DropdownItem>
          ))
        ) : (
          <S.DropdownItem key={0}>{notFoundMessage}</S.DropdownItem>
        )}
        <Frame width={listRef?.current?.offsetWidth} ref={frameRef}>
          {isEmpty ? (
            <S.DropdownItem key={0}>{notFoundMessage}</S.DropdownItem>
          ) : (
            maskedFilteredItems.map(({ id, name, type }, index) => (
              <S.DropdownItem key={index}>{name}</S.DropdownItem>
            ))
          )}
        </Frame>
      </S.DropdownList>

      {/* <S.Mark ref={markRef} left={markPosition.x} top={markPosition.y} /> */}
    </S.DropdownContainer>
  );
};

export default Object.assign(InputDropdown, {
  Multiple: InputDropdownMultiple,
});
